/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'

import ThreeDMaterialsIcon from '@spectrum-icons/workflow/3DMaterials'
import LightIcon from '@spectrum-icons/workflow/Light'
import EffectsIcon from '@spectrum-icons/workflow/Effects'
import MovieCameraIcon from '@spectrum-icons/workflow/MovieCamera'
import ReplayIcon from '@spectrum-icons/workflow/Replay'
import SettingsIcon from '@spectrum-icons/workflow/Settings'
import TargetIcon from '@spectrum-icons/workflow/Target'
import AddCircleIcon from '@spectrum-icons/workflow/AddCircle'
import DebugIcon from '@spectrum-icons/workflow/Bug'

import type { A3dModelCamera, A3dModel } from '@a3d-viewer/viewer'
import { A3dViewerElement, ModelInputAnimationStatus } from '@a3d-viewer/viewer'

import { ModelType } from '../App'
import { A3dHotSpotSurfaceProps, viewerAtom } from '../config'
import { AnimationsPanel } from '../panels/AnimationsPanel'
import { CameraPanel } from '../panels/CameraPanel'
// import { EffectsPanel } from '../panels/EffectsPanel'
import { EnvironmentPanel } from '../panels/EnvironmentPanel'
import { MultiModelsPanel } from '../panels/MultiModelsPanel'
import { HotSpotsPanel } from '../panels/HotSpotsPanel'
import { ModelPanel } from '../panels/ModelPanel'
import { SettingsPanel } from '../panels/SettingsPanel'
import HotSpot from './HotSpot'
import MenuContainer from './MenuContainer'

import '@a3d-extensions/a3d-inspector'
import '@a3d-extensions/a3d-grid'
import '@a3d-viewer/viewer'
import '@a3d-viewer/types'
import '@a3d-ui/toolbar'
import '@a3d-ui/loading-screen'
import { CreateHotSpotQueryForPickingInfo } from '@babylonjs/core/Meshes/abstractMesh.hotSpot'
import { EffectsPanel } from '../panels/EffectsPanel'
import { DebugPanel } from '../panels/DebugPanel'

type ViewerProps = {
  model: ModelType | null
  toggleFullscreen?: () => void
  onDomChange?: (elt: A3dViewerElement) => void
  onStateChange?: (newState: string) => void
  onError?: (e: Event) => void
}

function ViewerWC({ model, onDomChange, onError }: ViewerProps) {
  const [viewer, setViewer] = useAtom(viewerAtom)
  const viewerRef = useRef<A3dViewerElement>(null)
  const modelRef = useRef<A3dModel>(null)
  const [meshCameras, setMeshCameras] = useState<A3dModelCamera[]>([])

  // TODO : maybe something to put directly in the a-3d-viewer component ?
  const stopWheelPropagation = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
  }

  useEffect(() => {
    if (!viewerRef.current) return undefined
    onDomChange?.(viewerRef.current)
  })

  const handleReset = () => {
    if (viewerRef.current?.viewerDetails) {
      viewerRef.current.viewerDetails.viewer.resetCamera()
    }
  }

  // click event listener
  useEffect(() => {
    if (!viewerRef.current) return undefined
    const viewerEle = viewerRef.current

    viewerEle.addEventListener('click', handleClick, false)

    return () => {
      viewerEle.removeEventListener('click', handleClick)
    }
  }, [viewerRef.current, viewer.hotSpot.addHotSpotOnNextClick])

  useEffect(() => {
    const viewerEle = viewerRef.current
    const model = viewer.model
    const selectedAnimation = model.selectedAnimation

    if (viewerEle && selectedAnimation) {
      const index: number = model.animations.indexOf(selectedAnimation)
      viewerEle.selectedAnimation = index
    }
  }, [viewer.model.selectedAnimation])

  useEffect(() => {
    if (!viewerRef.current) return undefined
    const viewerEle = viewerRef.current

    viewerEle.addEventListener('wheel', stopWheelPropagation, {
      passive: false,
    })
    viewerEle.addEventListener('sceneloaded', handleReadyEvent)
    viewerEle.addEventListener('modelchange', handleModelChangeEvent)
    viewerEle.addEventListener('error', handleError)

    return () => {
      viewerEle.removeEventListener('wheel', stopWheelPropagation)
      viewerEle.removeEventListener('sceneloaded', handleReadyEvent)
      viewerEle.removeEventListener('modelchange', handleModelChangeEvent)
      viewerEle.removeEventListener('error', handleError)
    }
  }, [viewerRef.current])

  function handleError(e: Event) {
    onError?.(e)
  }

  const handleReadyEvent = () => {
    const viewerEle = viewerRef.current
    const viewerObj = viewerEle!.viewerDetails?.viewer

    if (!viewerObj) {
      return
    }

    const cameras = viewerObj?.cameras()
    setMeshCameras(cameras)

    setViewer((viewer) => {
      return { ...viewer, sceneReady: true }
    })
  }

  const handleModelChangeEvent = () => {
    const viewerEle = viewerRef.current
    const viewerObj = viewerEle!.viewerDetails?.viewer

    if (!viewerObj) {
      return
    }

    const models = viewerObj.loadedModels()
    // For the main demo app, set the only model as active to easily have access to animations
    if (models && models.length > 0) {
      const model = models[0]

      if (
        model.assetContainer.animationGroups.length > 0 &&
        viewerEle?.viewerDetails.model === null
      ) {
        console.debug('Demo - animations found set first model loaded active')
        model.makeActive()
        viewerObj.reset('camera', 'animation', 'material-variant')
        const animations = viewerObj.animations
        setViewer((viewer) => {
          return {
            ...viewer,
            model: {
              ...viewer.model,
              animations: animations,
              selectedAnimation: animations[0],
              animationStatus: viewer.animationAutoPlay
                ? ModelInputAnimationStatus.PLAY
                : undefined,
              disabledAnimations: true,
            },
          }
        })
      }
    }
  }

  const addHotSpot = async (event: MouseEvent): Promise<boolean> => {
    if (!viewerRef.current?.viewerDetails) return false
    if (!modelRef.current || !modelRef.current.model) return false
    const viewerObj = viewerRef.current.viewerDetails?.viewer

    const pickingInfo = await viewerRef.current.viewerDetails.pick(
      event.offsetX,
      event.offsetY
    )
    if (!pickingInfo || !pickingInfo.pickedMesh) return false
    const hotSpotQuery = CreateHotSpotQueryForPickingInfo(pickingInfo)
    if (!hotSpotQuery) return false

    const model = modelRef.current.model
    const hotSpotProps: A3dHotSpotSurfaceProps = {
      modelIndex: 0,
      meshIndex: model.assetContainer.meshes.indexOf(pickingInfo.pickedMesh),
      pointIndex: [
        hotSpotQuery.pointIndex[0],
        hotSpotQuery.pointIndex[1],
        hotSpotQuery.pointIndex[2],
      ],
      barycentric: [
        hotSpotQuery.barycentric[0],
        hotSpotQuery.barycentric[1],
        hotSpotQuery.barycentric[2],
      ],
    }

    // if the meshIndex is -1, it means that the mesh is not in the active model
    if (hotSpotProps.meshIndex === -1) {
      const models = viewerObj.loadedModels()
      for (let i = 0; i < models.length; i++) {
        const meshIndex = models[i].assetContainer.meshes.indexOf(
          pickingInfo.pickedMesh
        )
        if (meshIndex !== -1) {
          hotSpotProps.meshIndex = meshIndex
          hotSpotProps.modelIndex = i
          break
        }
      }
    }

    if (viewer.hotSpot.addHotSpotWithCameraOrbit) {
      const camera = viewerRef.current.viewerDetails.camera
      hotSpotProps.cameraOrbit = [camera.alpha, camera.beta, camera.radius]
    }

    setViewer({
      ...viewer,
      hotSpot: {
        ...viewer.hotSpot,
        addHotSpotOnNextClick: false,
        a3dHotSpotsProps: [...viewer.hotSpot.a3dHotSpotsProps, hotSpotProps],
      },
    })

    return true
  }

  const handleClick = async (event: MouseEvent) => {
    if (viewer.hotSpot.addHotSpotOnNextClick) {
      if (!(await addHotSpot(event))) {
        setViewer({
          ...viewer,
          hotSpot: { ...viewer.hotSpot, addHotSpotOnNextClick: false },
        })
      }
    }
  }

  const getSurfaceHotSpots = (modelIndex: number, color: string) => {
    return (
      viewer.hotSpot.a3dHotSpotsProps.length > 0 &&
      viewer.hotSpot.a3dHotSpotsProps.map(
        (value, index) =>
          value.hasOwnProperty('meshIndex') &&
          (value as A3dHotSpotSurfaceProps).modelIndex === modelIndex && (
            <a3d-hot-spot key={`a3dHotSpotSurface-${index}`} {...value}>
              <HotSpot color={color} />
            </a3d-hot-spot>
          )
      )
    )
  }

  const getAdditionalModels = (layer: string, color: string) => {
    return (
      viewer.additionalModels.length > 0 &&
      viewer.additionalModels.map(
        (model, i) =>
          model.layer === layer &&
          model.src && (
            <a3d-model
              key={`additionalNodeModel-${i}`}
              src={model.src}
              loader={model.loader as any}
              position={[
                model.translation.x,
                model.translation.y,
                model.translation.z,
              ]}
              rotation={[model.rotation.x, model.rotation.y, model.rotation.z]}
              scaling={[model.scaling.x, model.scaling.y, model.scaling.z]}
            >
              {getSurfaceHotSpots(i + 1, color)}
            </a3d-model>
          )
      )
    )
  }

  return (
    <>
      {model?.fileAsDataUrl && (
        <a3d-viewer
          ref={viewerRef}
          camera-orbit="0.9 0.9 auto"
          {...(viewer.animationAutoPlay && { 'animation-auto-play': 'true' })}
          {...(viewer.color && { 'clear-color': viewer.color })}
          noCameraReframe={!viewer.cameraReframe}
          noCameraLimit={!viewer.cameraLimit}
          ao-strength={viewer.rendererOptions.aoStrength?.value}
        >
          {viewer.grid && <a3d-grid></a3d-grid>}
          <a3d-environment
            src={viewer.envSrc}
            rotation-y={viewer.envRotation}
            intensity={viewer.envIntensity}
            {...(viewer.envBlurLevel > 0 && {
              'blur-level': viewer.envBlurLevel,
            })}
            {...(viewer.envVisibility && { visible: true })}
          ></a3d-environment>
          {(viewer.camera.orbitOn || viewer.camera.targetOn) && (
            <a3d-camera
              {...(viewer.camera.orbitOn && {
                orbit: [
                  viewer.camera.orbit.alpha,
                  viewer.camera.orbit.beta,
                  viewer.camera.orbit.radius,
                ],
              })}
              {...(viewer.camera.targetOn && {
                target: [
                  viewer.camera.target.x,
                  viewer.camera.target.y,
                  viewer.camera.target.z,
                ],
              })}
            ></a3d-camera>
          )}
          {viewer.camera.selectedCamera && (
            <a3d-camera camera-name={viewer.camera.selectedCamera}></a3d-camera>
          )}

          <a3d-node
            position={[
              viewer.node.translation.x,
              viewer.node.translation.y,
              viewer.node.translation.z,
            ]}
            rotation={[
              viewer.node.rotation.x,
              viewer.node.rotation.y,
              viewer.node.rotation.z,
            ]}
            scaling={[
              viewer.node.scaling.x,
              viewer.node.scaling.y,
              viewer.node.scaling.z,
            ]}
          >
            <a3d-model
              ref={modelRef}
              src={model?.fileAsDataUrl}
              loader={model?.extension}
              position={[
                viewer.model.translation.x,
                viewer.model.translation.y,
                viewer.model.translation.z,
              ]}
              rotation={[
                viewer.model.rotation.x,
                viewer.model.rotation.y,
                viewer.model.rotation.z,
              ]}
              scaling={[
                viewer.model.scaling.x,
                viewer.model.scaling.y,
                viewer.model.scaling.z,
              ]}
              {...(viewer.model.selectedAnimation && {
                'selected-animation': viewer.model.animations?.indexOf(
                  viewer.model.selectedAnimation
                ),
              })}
              animation-status={viewer.model.animationStatus}
            >
              {getSurfaceHotSpots(0, 'green')}
            </a3d-model>
            {getAdditionalModels('node', 'yellow')}
          </a3d-node>

          {viewer.hotSpot.a3dHotSpotsProps.length > 0 &&
            viewer.hotSpot.a3dHotSpotsProps.map(
              (value, index) =>
                !value.hasOwnProperty('meshIndex') && (
                  <a3d-hot-spot key={`a3dHotSpotWorld-${index}`} {...value}>
                    <HotSpot color="blue" />
                  </a3d-hot-spot>
                )
            )}
          {getAdditionalModels('viewer', 'orange')}
          {viewer.toolbar.enable && <a3d-toolbar></a3d-toolbar>}
          <a3d-inspector visible={viewer.inspector.enable}></a3d-inspector>
          <a3d-loading-screen></a3d-loading-screen>
        </a3d-viewer>
      )}

      {viewer.sceneReady && (
        <MenuContainer
          className={viewer.inspector.enable ? 'left-[300px]' : 'left-0'}
          top={[
            [
              {
                key: 'General',
                icon: <ThreeDMaterialsIcon />,
                component: <ModelPanel />,
              },
              {
                key: 'Camera',
                icon: <MovieCameraIcon />,
                component: (
                  <CameraPanel
                    handleReset={handleReset}
                    cameras={meshCameras}
                  />
                ),
              },
              {
                key: 'Environment',
                icon: <LightIcon />,
                component: <EnvironmentPanel />,
              },
              {
                key: 'Effects',
                icon: <EffectsIcon />,
                component: <EffectsPanel />,
              },
              {
                key: 'MultiModels',
                icon: <AddCircleIcon />,
                component: <MultiModelsPanel />,
              },
              {
                key: 'Debug',
                icon: <DebugIcon />,
                component: <DebugPanel></DebugPanel>,
              },
            ],
            [
              {
                key: 'HotSpots',
                icon: <TargetIcon />,
                component: <HotSpotsPanel viewerEle={viewerRef.current} />,
              },
              ...(viewer.model.animations.length > 0
                ? [
                    {
                      key: 'Animations',
                      icon: <ReplayIcon />,
                      component: <AnimationsPanel />,
                    },
                  ]
                : []),
            ],
          ]}
          bottom={{
            key: 'Settings',
            icon: <SettingsIcon />,
            component: <SettingsPanel />,
          }}
        ></MenuContainer>
      )}
    </>
  )
}

export default ViewerWC
